import Dexie, { type EntityTable } from "dexie";
import type { PersistedTask, SyncList } from "@/stores/taskStore";
import type { PersistedOp } from "@/lib/mutators/types";

export interface MetaRow {
  key: string;
  value: unknown;
}

// A task pulled as removed: `at` (ms) only serves pruning.
export interface TombstoneRow {
  id: string;
  pulledAt: string;
  at: number;
}

export const localDb = new Dexie("revolver-local") as Dexie & {
  tasks: EntityTable<PersistedTask, "id">;
  lists: EntityTable<SyncList, "id">;
  meta: EntityTable<MetaRow, "key">;
  tombstones: EntityTable<TombstoneRow, "id">;
  outbox: EntityTable<PersistedOp, "seq">;
};

localDb.version(1).stores({
  tasks: "id",
  lists: "id",
  meta: "key",
});

localDb.version(2).stores({
  tombstones: "id, at",
});

// One numbering for every tab: the server applies a client's ops in seq order, once each.
localDb.version(3).stores({
  outbox: "++seq",
});

export const SYNC_LOCK = "revolver-sync";

// Legacy IndexedDB database of page snapshots; it holds task titles, so it must not linger.
const LEGACY_SNAPSHOTS_DB = "revolver-snapshots";

export function deleteLegacySnapshots(): void {
  try {
    const request = globalThis.indexedDB?.deleteDatabase(LEGACY_SNAPSHOTS_DB);
    if (request) request.onerror = (event) => event.preventDefault();
  } catch {
    // Storage disabled: there is nothing to delete either.
  }
}

// Lowers the chance the browser evicts unsent changes under storage pressure (Safari above all).
export function requestPersistentStorage(): void {
  const storage = typeof navigator === "undefined" ? undefined : navigator.storage;
  storage?.persist?.().catch(() => {});
}

// Tabs share one IndexedDB copy, so whatever writes it takes turns.
export async function withSyncLock(run: () => Promise<void>): Promise<void> {
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  if (!locks?.request) return run();
  await locks.request(SYNC_LOCK, run);
}

// Meta rows that belong to the outbox. The outbox outlives the copy: ops not sent yet wait
// for their user to sign in again.
// ackedThrough: the highest seq the server confirmed while no tab could pull its effects.
export const OUTBOX_META = { clientId: "clientId", sentThrough: "sentThrough", userId: "outboxUserId", ackedThrough: "ackedThrough" } as const;
const OUTBOX_META_KEYS = Object.values(OUTBOX_META);

// IndexedDB is unavailable in private windows and when site storage is disabled; the
// in-memory store still works, it just starts empty on the next boot.
export async function clearLocalDb(): Promise<void> {
  try {
    await withSyncLock(() =>
      localDb.transaction("rw", [localDb.tasks, localDb.lists, localDb.meta, localDb.tombstones], async () => {
        await Promise.all([
          localDb.tasks.clear(),
          localDb.lists.clear(),
          localDb.tombstones.clear(),
          localDb.meta.where("key").noneOf(OUTBOX_META_KEYS).delete(),
        ]);
      }),
    );
  } catch {
    // ignored
  }
}

// `keepFor`: an outbox that already belongs to this user is kept (another tab got there first).
export async function discardOutbox(keepFor?: string): Promise<void> {
  try {
    await withSyncLock(() =>
      localDb.transaction("rw", [localDb.outbox, localDb.meta], async () => {
        if (keepFor !== undefined && (await localDb.meta.get(OUTBOX_META.userId))?.value === keepFor) return;
        await Promise.all([localDb.outbox.clear(), localDb.meta.bulkDelete(OUTBOX_META_KEYS)]);
      }),
    );
  } catch {
    // ignored
  }
}

// Ops waiting for their user to sign in again; an outbox without an owner is never sent.
export async function countUnsentOps(): Promise<number> {
  try {
    const [owner, acked] = await localDb.meta.bulkGet([OUTBOX_META.userId, OUTBOX_META.ackedThrough]);
    return owner ? await localDb.outbox.where("seq").above(Number(acked?.value ?? 0)).count() : 0;
  } catch {
    return 0;
  }
}
